/**
 * NMEA0183 の解析。
 *
 * 既存 GoalView が使うのは $GPRMC と $GPGGA だけだが、実 GPS ログには
 * 次世代版に必要な情報が既に記録されている（docs/01 §11）。
 *   $GPGST … 位置・高度の誤差推定値 → 風推定の品質重み
 *   $GPZDA … UTC 日付＋時刻       → 絶対時刻の復元
 *   $GPVTG … 対地速度・方位
 * それらも解析できるようにしてある。
 */

/** ノット → m/s */
const KNOT_TO_MPS = 0.514444444;

/** `$` を除き `*` の手前までの XOR。大文字16進2桁 */
export function nmeaChecksum(sentence: string): string {
  const body = sentence.replace(/^\$/, '').replace(/\*.*$/, '');
  let c = 0;
  for (let i = 0; i < body.length; i++) c ^= body.charCodeAt(i) & 0xff;
  return c.toString(16).toUpperCase().padStart(2, '0');
}

/** チェックサムを検証する。`*XX` が無い場合は false */
export function verifyChecksum(sentence: string): boolean {
  const m = /\*([0-9A-Fa-f]{2})\s*$/.exec(sentence);
  return m ? m[1].toUpperCase() === nmeaChecksum(sentence) : false;
}

/** チェックサムを付け直す（既にあれば置き換える） */
export function withChecksum(sentence: string): string {
  const bare = sentence.replace(/\*.*$/, '');
  return `${bare}*${nmeaChecksum(bare)}`;
}

/**
 * トーカーIDを $GP に正規化してチェックサムを振り直す。
 * GoalView は送信時にこれを行い（`GoalView.cpp:1214`）、受信側は
 * $GPGGA / $GPRMC しか受け付けないため、互換送信には必須。
 */
export function normalizeTalker(sentence: string): string | null {
  const m = /^\$G.([A-Z]{3})(,.*)?$/.exec(sentence.replace(/\*.*$/, ''));
  return m ? withChecksum(`$GP${m[1]}${m[2] ?? ''}`) : null;
}

/** センテンス種別（先頭からカンマ直前まで）。例: '$GPRMC', '$_TVCIRCLE' */
export function sentenceType(sentence: string): string {
  const comma = sentence.indexOf(',');
  return comma > 0 ? sentence.slice(0, comma) : sentence.replace(/\*.*$/, '');
}

/** `ddmm.mmmm` 形式を度に変換する。半球指定で符号を付ける */
export function nmeaCoordToDeg(value: string, hemisphere: string): number | null {
  if (!value) return null;
  const v = Number(value);
  if (!Number.isFinite(v)) return null;
  const deg = Math.floor(v / 100);
  const min = v - deg * 100;
  const d = deg + min / 60;
  return hemisphere === 'S' || hemisphere === 'W' ? -d : d;
}

/** `nmeaCoordToDeg` の逆。度 → `ddmm.mmmm` / `dddmm.mmmm` ＋ 半球 */
export function degToNmeaCoord(
  deg: number,
  axis: 'lat' | 'lon',
): { value: string; hemisphere: string } {
  const hemisphere = axis === 'lat' ? (deg < 0 ? 'S' : 'N') : deg < 0 ? 'W' : 'E';
  const abs = Math.abs(deg);
  const whole = Math.floor(abs);
  const min = (abs - whole) * 60;
  // 緯度は2桁、経度は3桁の度（NMEA0183の定め）
  const degDigits = axis === 'lat' ? 2 : 3;
  const value = `${String(whole).padStart(degDigits, '0')}${min.toFixed(4).padStart(7, '0')}`;
  return { value, hemisphere };
}

const num = (s: string | undefined): number | null => {
  if (s === undefined || s === '') return null;
  const v = Number(s);
  return Number.isFinite(v) ? v : null;
};

export interface RmcData {
  /** UTC 時刻 hhmmss.ss を「その日の秒数」に直したもの */
  utcSeconds: number | null;
  /** UTC 日付 ddmmyy をパースしたもの。RMC が持つ唯一の日付情報 */
  utcDate: { year: number; month: number; day: number } | null;
  valid: boolean;
  lat: number | null;
  lon: number | null;
  /** 対地速度 [m/s]（元はノット） */
  speed: number | null;
  /** 進行方向 [deg] */
  heading: number | null;
}

export function parseRmc(sentence: string): RmcData | null {
  const f = sentence.replace(/\*.*$/, '').split(',');
  if (!/^\$G.RMC$/.test(f[0])) return null;
  return {
    utcSeconds: parseUtcTime(f[1]),
    utcDate: parseUtcDate(f[9]),
    valid: f[2] === 'A',
    lat: nmeaCoordToDeg(f[3], f[4]),
    lon: nmeaCoordToDeg(f[5], f[6]),
    speed: num(f[7]) === null ? null : (num(f[7]) as number) * KNOT_TO_MPS,
    heading: num(f[8]),
  };
}

export interface GgaData {
  utcSeconds: number | null;
  lat: number | null;
  lon: number | null;
  /** 測位品質。0=無効, 1=単独測位, 2=DGPS ... */
  fixQuality: number | null;
  satellites: number | null;
  hdop: number | null;
  /** 海抜高度 [m]。GoalView が唯一使っている高度 */
  altMsl: number | null;
  /** ジオイド高 [m] */
  geoidSeparation: number | null;
}

export function parseGga(sentence: string): GgaData | null {
  const f = sentence.replace(/\*.*$/, '').split(',');
  if (!/^\$G.GGA$/.test(f[0])) return null;
  return {
    utcSeconds: parseUtcTime(f[1]),
    lat: nmeaCoordToDeg(f[2], f[3]),
    lon: nmeaCoordToDeg(f[4], f[5]),
    fixQuality: num(f[6]),
    satellites: num(f[7]),
    hdop: num(f[8]),
    altMsl: num(f[9]),
    geoidSeparation: num(f[11]),
  };
}

export interface GnsData {
  utcSeconds: number | null;
  lat: number | null;
  lon: number | null;
  /** 測位モード。'N' は無測位 */
  mode: string;
  satellites: number | null;
  hdop: number | null;
  altMsl: number | null;
  geoidSeparation: number | null;
}

/**
 * $GNS — マルチGNSS測位。
 *
 * ★ 実ログでは **RMC がほとんど出力されておらず、位置の主供給源は GNS だった**
 *   （57分のログで RMC が16本に対し GNS/GGA/VTG は各3446本 = 1Hz）。
 *   RMC だけに頼る実装は実運用の受信機設定で機能しない。docs/01 §11 参照。
 */
export function parseGns(sentence: string): GnsData | null {
  const f = sentence.replace(/\*.*$/, '').split(',');
  if (!/^\$G.GNS$/.test(f[0])) return null;
  return {
    utcSeconds: parseUtcTime(f[1]),
    lat: nmeaCoordToDeg(f[2], f[3]),
    lon: nmeaCoordToDeg(f[4], f[5]),
    mode: f[6] ?? '',
    satellites: num(f[7]),
    hdop: num(f[8]),
    altMsl: num(f[9]),
    geoidSeparation: num(f[10]),
  };
}

export interface GstData {
  utcSeconds: number | null;
  /** 緯度方向の標準偏差 [m] */
  latSigma: number | null;
  /** 経度方向の標準偏差 [m] */
  lonSigma: number | null;
  /** 高度の標準偏差 [m] */
  altSigma: number | null;
}

/** $GPGST — 誤差推定値。開発メモの「GPS精度」に対応する唯一の情報源 */
export function parseGst(sentence: string): GstData | null {
  const f = sentence.replace(/\*.*$/, '').split(',');
  if (!/^\$G.GST$/.test(f[0])) return null;
  return {
    utcSeconds: parseUtcTime(f[1]),
    latSigma: num(f[6]),
    lonSigma: num(f[7]),
    altSigma: num(f[8]),
  };
}

/** $GPZDA — UTC 日付＋時刻。既存プロトコルに欠けている絶対時刻の供給源 */
export function parseZda(sentence: string): { t: number | null } | null {
  const f = sentence.replace(/\*.*$/, '').split(',');
  if (!/^\$G.ZDA$/.test(f[0])) return null;
  const secs = parseUtcTime(f[1]);
  const day = num(f[2]);
  const month = num(f[3]);
  const year = num(f[4]);
  if (secs === null || day === null || month === null || year === null) return { t: null };
  return { t: Date.UTC(year, month - 1, day) + secs * 1000 };
}

export interface VtgData {
  /** 真方位 [deg] */
  trackTrue: number | null;
  /** 磁方位 [deg] */
  trackMagnetic: number | null;
  /** 対地速度 [m/s] */
  speed: number | null;
}

export function parseVtg(sentence: string): VtgData | null {
  const f = sentence.replace(/\*.*$/, '').split(',');
  if (!/^\$G.VTG$/.test(f[0])) return null;
  // km/h フィールド（f[7]）があればそれを、無ければノット（f[5]）を使う
  const kmh = num(f[7]);
  const knot = num(f[5]);
  return {
    trackTrue: num(f[1]),
    trackMagnetic: num(f[3]),
    speed: kmh !== null ? (kmh * 1000) / 3600 : knot !== null ? knot * KNOT_TO_MPS : null,
  };
}

/** `hhmmss.ss` → その日の経過秒 */
export function parseUtcTime(value: string | undefined): number | null {
  if (!value || value.length < 6) return null;
  const h = Number(value.slice(0, 2));
  const m = Number(value.slice(2, 4));
  const s = Number(value.slice(4));
  if (!Number.isFinite(h) || !Number.isFinite(m) || !Number.isFinite(s)) return null;
  return h * 3600 + m * 60 + s;
}

/** `ddmmyy` → 年月日。RMC の日付フィールドは2桁年なので 2000 年代とみなす */
export function parseUtcDate(
  value: string | undefined,
): { year: number; month: number; day: number } | null {
  if (!value || value.length !== 6) return null;
  const day = Number(value.slice(0, 2));
  const month = Number(value.slice(2, 4));
  const yy = Number(value.slice(4, 6));
  if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(yy)) return null;
  return { year: 2000 + yy, month, day };
}

/** `その日の経過秒` → `hhmmss.ss`（parseUtcTime の逆） */
function formatUtcTime(utcSeconds: number): string {
  const s = ((utcSeconds % 86400) + 86400) % 86400;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}${String(m).padStart(2, '0')}${sec.toFixed(2).padStart(5, '0')}`;
}

export interface BuildGgaInput {
  /** UTC epoch ミリ秒 */
  t: number;
  lat: number;
  lon: number;
  /** 海抜高度 [m]。無ければ空欄で送る */
  altMsl?: number | null;
  /** 測位品質。既定 1（単独測位） */
  fixQuality?: number;
  satellites?: number | null;
  hdop?: number | null;
}

/**
 * $GPGGA を組み立てる。`parseGga` の逆方向。
 *
 * 自機位置の送信（GPS または手動入力）で使う。既存プロトコルは $GPGGA と
 * $GPRMC しか受け付けないため（`normalizeTalker` 参照）、talker は最初から GP にする。
 */
export function buildGga(input: BuildGgaInput): string {
  const utcSeconds = Math.floor(input.t / 1000) % 86400;
  const lat = degToNmeaCoord(input.lat, 'lat');
  const lon = degToNmeaCoord(input.lon, 'lon');
  const alt = input.altMsl != null ? input.altMsl.toFixed(1) : '';
  const fields = [
    formatUtcTime(utcSeconds),
    lat.value,
    lat.hemisphere,
    lon.value,
    lon.hemisphere,
    String(input.fixQuality ?? 1),
    input.satellites != null ? String(input.satellites) : '',
    input.hdop != null ? input.hdop.toFixed(1) : '',
    alt,
    alt ? 'M' : '',
    '', // ジオイド高。手入力・GPS実測とも通常は得られない
    '',
    '', // DGPS 関連
    '',
  ];
  return withChecksum(`$GPGGA,${fields.join(',')}`);
}

export interface BuildRmcInput {
  /** UTC epoch ミリ秒 */
  t: number;
  lat: number;
  lon: number;
  /** 対地速度 [m/s]。無ければ空欄 */
  speed?: number | null;
  /** 進行方向 [deg]。無ければ空欄 */
  heading?: number | null;
}

/** $GPRMC を組み立てる。`parseRmc` の逆方向 */
export function buildRmc(input: BuildRmcInput): string {
  const d = new Date(input.t);
  const utcSeconds = Math.floor(input.t / 1000) % 86400;
  const lat = degToNmeaCoord(input.lat, 'lat');
  const lon = degToNmeaCoord(input.lon, 'lon');
  const knot = input.speed != null ? input.speed / KNOT_TO_MPS : null;
  const ddmmyy =
    String(d.getUTCDate()).padStart(2, '0') +
    String(d.getUTCMonth() + 1).padStart(2, '0') +
    String(d.getUTCFullYear() % 100).padStart(2, '0');
  const fields = [
    formatUtcTime(utcSeconds),
    'A', // 測位状態。手入力・GPS実測とも「有効」として送る
    lat.value,
    lat.hemisphere,
    lon.value,
    lon.hemisphere,
    knot != null ? knot.toFixed(1) : '',
    input.heading != null ? input.heading.toFixed(1) : '',
    ddmmyy,
    '',
    '',
    // NMEA 2.3以降の測位モード指標（磁気偏差の次に付く12番目のフィールド）。
    // 実機（例: Yudai端末）はこれを付けて送ってきており、無いのはGV4D側だけ
    // だった（docs/08 §61）。原典GoalViewの受信側実装は固定オフセットで
    // 先頭のフィールドしか見ていないため実害は無いはずだが、本家サーバーの
    // 集計・アーカイブ生成側が可変長パースをしていて11フィールドの行を
    // 読み違えている可能性を疑い、実機に合わせて追加した
    'A',
  ];
  return withChecksum(`$GPRMC,${fields.join(',')}`);
}

/**
 * 2点の緯度経度から進行方向を求める。
 *
 * GoalView は RMC の針路フィールドが空のとき `heading_interval` 秒前の位置と
 * 比較して算出している（`myCalcHeading`）。同じ場面で使う。
 */
export function headingBetween(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const rad = Math.PI / 180;
  const dLon = (lon2 - lon1) * rad;
  const y = Math.sin(dLon) * Math.cos(lat2 * rad);
  const x =
    Math.cos(lat1 * rad) * Math.sin(lat2 * rad) -
    Math.sin(lat1 * rad) * Math.cos(lat2 * rad) * Math.cos(dLon);
  const deg = (Math.atan2(y, x) * 180) / Math.PI;
  return (deg + 360) % 360;
}
